import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import ImageUploadField from "@/components/ImageUploadField";

const AD_SLOTS = ["ribbon", "banner", "rail-left", "rail-right", "search-top", "search-mid"];
const BLANK_AD = {
  slot: "ribbon", title: "", subtitle: "", url: "", color: "#dc2626",
  icon: "", tag: "", image_url: "", is_active: true, cities: [], categories: [],
};

export default function AdsPanel() {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [allCities, setAllCities] = useState([]);
  const [allCategories, setAllCategories] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/ads");
      setAds(data || []);
    } catch (e) {
      toast.error("Could not load ads");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    api.get("/cities")
      .then((r) => setAllCities((r.data || []).map((c) => (typeof c === "string" ? c : c.name))))
      .catch(() => {});
    api.get("/categories")
      .then((r) => setAllCategories((r.data || []).map((c) => (typeof c === "string" ? c : c.name))))
      .catch(() => {});
  }, []);

  const save = async () => {
    if (!editing.title || !editing.slot) { toast.error("Slot + title required"); return; }
    try {
      if (editing.ad_id) { await api.patch(`/admin/ads/${editing.ad_id}`, editing); toast.success("Ad updated"); }
      else { await api.post("/admin/ads", editing); toast.success("Ad created"); }
      setEditing(null);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const toggle = async (ad) => {
    try { await api.patch(`/admin/ads/${ad.ad_id}`, { is_active: !ad.is_active }); await load(); }
    catch (e) { toast.error("Toggle failed"); }
  };

  const remove = async (ad) => {
    if (!window.confirm(`Delete ad "${ad.title}"?`)) return;
    try { await api.delete(`/admin/ads/${ad.ad_id}`); toast.success("Ad deleted"); await load(); }
    catch (e) { toast.error("Delete failed"); }
  };

  const set = (k, v) => setEditing((s) => ({ ...s, [k]: v }));

  return (
    <Card className="p-6 border-border/60">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-heading text-xl">Manage ads</h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Slots without an active, targeting-matched ad are hidden in the UI. Impressions and clicks track per ad — CTR is shown live in the table.
          </p>
        </div>
        <Button onClick={() => setEditing(BLANK_AD)} className="rounded-full" data-testid="admin-ad-new-btn">
          + New ad
        </Button>
      </div>

      <div className="mt-6 overflow-x-auto" data-testid="admin-ad-table">
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : ads.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center" data-testid="admin-ad-empty">
            No ads yet — all slots are using the built-in samples.
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
              <tr className="border-b border-border/60">
                <th className="text-left py-2">Slot</th>
                <th className="text-left">Title</th>
                <th className="text-left">Targeting</th>
                <th className="text-left">URL</th>
                <th className="text-center">Impr / Clk · CTR</th>
                <th className="text-center">Active</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {ads.map((a) => {
                const imp = a.impressions || 0;
                const clk = a.clicks || 0;
                const ctr = imp > 0 ? ((clk / imp) * 100).toFixed(1) : "0.0";
                return (
                  <tr key={a.ad_id} className="border-b border-border/60" data-testid={`admin-ad-row-${a.ad_id}`}>
                    <td className="py-2"><Badge variant="secondary">{a.slot}</Badge></td>
                    <td className="py-2">
                      <div className="flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full" style={{ background: a.color || "#999" }} />
                        <div>
                          <div className="font-medium">{a.title}</div>
                          {a.subtitle && <div className="text-xs text-muted-foreground">{a.subtitle}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="py-2 text-xs">
                      <div className="flex flex-col gap-0.5" data-testid={`admin-ad-targeting-${a.ad_id}`}>
                        <span className="text-muted-foreground">
                          Cities:{" "}
                          {(a.cities || []).length === 0
                            ? <em className="not-italic text-foreground/70">All</em>
                            : (a.cities || []).slice(0, 2).join(", ") + ((a.cities || []).length > 2 ? ` +${a.cities.length - 2}` : "")}
                        </span>
                        <span className="text-muted-foreground">
                          Categories:{" "}
                          {(a.categories || []).length === 0
                            ? <em className="not-italic text-foreground/70">All</em>
                            : (a.categories || []).slice(0, 2).join(", ") + ((a.categories || []).length > 2 ? ` +${a.categories.length - 2}` : "")}
                        </span>
                      </div>
                    </td>
                    <td className="py-2 text-xs text-muted-foreground max-w-[220px] truncate">{a.url}</td>
                    <td className="py-2 text-xs text-center font-mono" data-testid={`admin-ad-stats-${a.ad_id}`}>
                      <div className="leading-tight">
                        <div>{imp} / {clk}</div>
                        <div className="text-[10px] text-muted-foreground">{ctr}%</div>
                      </div>
                    </td>
                    <td className="py-2 text-center">
                      <button onClick={() => toggle(a)} data-testid={`admin-ad-toggle-${a.ad_id}`}>
                        <Badge variant={a.is_active ? "default" : "outline"}>{a.is_active ? "ON" : "OFF"}</Badge>
                      </button>
                    </td>
                    <td className="py-2 text-right whitespace-nowrap">
                      <Button size="sm" variant="ghost" onClick={() => setEditing(a)} data-testid={`admin-ad-edit-${a.ad_id}`}>Edit</Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(a)} data-testid={`admin-ad-delete-${a.ad_id}`} className="text-destructive">Delete</Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {editing && (
        <div className="mt-6 p-4 rounded-xl border border-border/60 bg-card/60" data-testid="admin-ad-editor">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Slot</label>
              <select className="mt-1 h-9 w-full rounded-md border border-input bg-background px-2 text-sm" value={editing.slot} onChange={(e) => set("slot", e.target.value)} data-testid="admin-ad-slot">
                {AD_SLOTS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Accent color</label>
              <input type="color" className="mt-1 h-9 w-full rounded-md border border-input bg-background" value={editing.color || "#dc2626"} onChange={(e) => set("color", e.target.value)} data-testid="admin-ad-color" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Title *</label>
              <Input className="mt-1" value={editing.title} onChange={(e) => set("title", e.target.value)} data-testid="admin-ad-title" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Subtitle</label>
              <Input className="mt-1" value={editing.subtitle || ""} onChange={(e) => set("subtitle", e.target.value)} data-testid="admin-ad-subtitle" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Link URL</label>
              <Input className="mt-1" placeholder="/vendors?category=Sweets+%26+Snacks  or  https://…" value={editing.url || ""} onChange={(e) => set("url", e.target.value)} data-testid="admin-ad-url" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Creative image (optional)</label>
              <div className="mt-1">
                <ImageUploadField
                  value={editing.image_url || ""}
                  onChange={(url) => set("image_url", url)}
                  testId="admin-ad-image"
                  aspect="aspect-[3/1]"
                />
              </div>
            </div>
            <div className="sm:col-span-2">
              <AdTargetingPicker
                label="Cities"
                hint="Leave empty to show in ALL cities. Select to restrict to specific cities."
                options={allCities}
                selected={editing.cities || []}
                onChange={(next) => set("cities", next)}
                testIdPrefix="admin-ad-cities"
              />
            </div>
            <div className="sm:col-span-2">
              <AdTargetingPicker
                label="Categories"
                hint="Leave empty to show in ALL categories. Select to restrict to specific categories."
                options={allCategories}
                selected={editing.categories || []}
                onChange={(next) => set("categories", next)}
                testIdPrefix="admin-ad-categories"
              />
            </div>
          </div>
          <div className="mt-4 flex items-center gap-2">
            <Button onClick={save} className="rounded-full" data-testid="admin-ad-save">{editing.ad_id ? "Update" : "Create"}</Button>
            <Button variant="outline" onClick={() => setEditing(null)} className="rounded-full" data-testid="admin-ad-cancel">Cancel</Button>
          </div>

          <div className="mt-6 pt-5 border-t border-border/60" data-testid="admin-ad-preview">
            <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground mb-3">Preview across slots</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {AD_SLOTS.map((s) => <AdSlotPreview key={s} slot={s} ad={editing} />)}
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

function AdSlotPreview({ slot, ad }) {
  const accent = ad.color || "#dc2626";
  if (slot === "ribbon") {
    return (
      <div className="rounded-lg overflow-hidden border border-border/60" data-testid={`admin-ad-preview-${slot}`}>
        <div className="text-[10px] uppercase tracking-[0.18em] px-2 py-1 bg-muted text-muted-foreground">ribbon</div>
        <div className="px-3 py-2 text-xs font-medium text-white flex items-center gap-2" style={{ background: accent }}>
          <span aria-hidden>📢</span> <span className="truncate">{ad.title || "Headline"}</span>
        </div>
      </div>
    );
  }
  if (slot === "banner") {
    return (
      <div className="rounded-lg overflow-hidden border border-border/60" data-testid={`admin-ad-preview-${slot}`}>
        <div className="text-[10px] uppercase tracking-[0.18em] px-2 py-1 bg-muted text-muted-foreground">banner</div>
        <div className="flex items-stretch">
          {ad.image_url ? <img src={ad.image_url} alt="" className="w-28 object-cover" /> : <div className="w-28 shrink-0" style={{ background: `${accent}22` }} />}
          <div className="p-3 text-xs flex-1">
            <div className="font-semibold leading-tight">{ad.title || "Banner headline"}</div>
            {ad.subtitle && <div className="text-muted-foreground">{ad.subtitle}</div>}
          </div>
        </div>
      </div>
    );
  }
  if (slot === "rail-left" || slot === "rail-right") {
    return (
      <div className="rounded-lg overflow-hidden border border-border/60 max-w-[180px]" data-testid={`admin-ad-preview-${slot}`}>
        <div className="text-[10px] uppercase tracking-[0.18em] px-2 py-1 bg-muted text-muted-foreground">{slot}</div>
        {ad.image_url ? (
          <img src={ad.image_url} alt="" className="w-full aspect-[4/3] object-cover" />
        ) : (
          <div className="w-full aspect-[4/3]" style={{ background: `${accent}22` }} />
        )}
        <div className="p-2">
          <div className="font-semibold text-xs leading-tight">{ad.title || "Rail headline"}</div>
          {ad.subtitle && <div className="text-[10px] text-muted-foreground">{ad.subtitle}</div>}
        </div>
      </div>
    );
  }
  return (
    <div className="rounded-lg overflow-hidden border border-border/60" data-testid={`admin-ad-preview-${slot}`}>
      <div className="text-[10px] uppercase tracking-[0.18em] px-2 py-1 bg-muted text-muted-foreground">{slot}</div>
      <div className="flex items-stretch">
        {ad.image_url ? <img src={ad.image_url} alt="" className="w-20 object-cover" /> : <div className="w-12 m-2 rounded flex items-center justify-center text-white text-xs" style={{ background: accent }}>AD</div>}
        <div className="p-2 flex-1 text-xs">
          <div className="text-[9px] uppercase tracking-[0.16em] font-semibold" style={{ color: accent }}>Sponsored</div>
          <div className="font-semibold leading-tight truncate">{ad.title || "Strip headline"}</div>
          {ad.subtitle && <div className="text-muted-foreground truncate">{ad.subtitle}</div>}
        </div>
      </div>
    </div>
  );
}

/**
 * Multi-select chip picker used for ad targeting (cities / categories).
 * Empty `selected` means "show everywhere".
 */
function AdTargetingPicker({ label, hint, options, selected, onChange, testIdPrefix }) {
  const [query, setQuery] = useState("");
  const toggle = (opt) => {
    if (selected.includes(opt)) onChange(selected.filter((s) => s !== opt));
    else onChange([...selected, opt]);
  };
  const filtered = (options || []).filter((o) => !query ? true : o.toLowerCase().includes(query.toLowerCase()));
  return (
    <div data-testid={`${testIdPrefix}-picker`}>
      <div className="flex items-baseline justify-between gap-3">
        <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">{label}</label>
        <span className="text-[10px] text-muted-foreground">
          {selected.length === 0 ? "Showing everywhere (no restriction)" : `${selected.length} selected`}
        </span>
      </div>
      {hint && <p className="text-[11px] text-muted-foreground mt-1">{hint}</p>}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`${testIdPrefix}-selected`}>
          {selected.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => toggle(s)}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs hover:bg-primary/20"
              data-testid={`${testIdPrefix}-chip-${s}`}
            >
              {s} <span aria-hidden>×</span>
            </button>
          ))}
          <button
            type="button"
            onClick={() => onChange([])}
            className="text-[10px] text-muted-foreground hover:text-foreground underline ml-1"
            data-testid={`${testIdPrefix}-clear`}
          >
            Clear all
          </button>
        </div>
      )}
      <Input
        className="mt-2"
        placeholder={`Filter ${label.toLowerCase()}…`}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        data-testid={`${testIdPrefix}-search`}
      />
      <div className="mt-2 max-h-40 overflow-y-auto rounded-md border border-border/60 bg-background/60 p-2 grid grid-cols-2 gap-1" data-testid={`${testIdPrefix}-options`}>
        {filtered.length === 0 ? (
          <div className="col-span-2 text-xs text-muted-foreground text-center py-2">No matches</div>
        ) : (
          filtered.map((o) => {
            const checked = selected.includes(o);
            return (
              <label
                key={o}
                className={`flex items-center gap-2 text-xs px-2 py-1 rounded cursor-pointer hover:bg-muted ${checked ? "bg-muted" : ""}`}
                data-testid={`${testIdPrefix}-option-${o}`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(o)}
                  className="h-3.5 w-3.5"
                />
                <span className="truncate">{o}</span>
              </label>
            );
          })
        )}
      </div>
    </div>
  );
}
